import React, {useMemo} from "react";
import {DatePicker, RangePickerPreset} from "peggirkit";
import {nl} from "date-fns/locale/nl";
import {DateRange} from "react-day-picker";
import {
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear
} from "date-fns";
import {useIntl} from "react-intl";
import {formatDate} from "@/utils/DateUtils";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {useSearchParams} from "react-router-dom";
import {setSearchParamsDateRange} from "@widgets/reports/Statistics";
import {SetStateFn} from "@/utils/SetStateUtils";

type Props = {
    dateRange: DateRange,
    showDatePicker: boolean,
    setShowDatePicker: SetStateFn<boolean>,
};

const StatisticsRangePicker = ({dateRange, showDatePicker, setShowDatePicker}: Props) => {
    const [, setSearchParams] = useSearchParams();
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const dateRangePresets: RangePickerPreset[] = useMemo(() => {
        const ranges: RangePickerPreset[] = [
            {
                name: intl.formatMessage({id: "today"}),
                range: {
                    from: startOfDay(new Date()),
                    to: endOfDay(new Date())
                },
            },
            {
                name: intl.formatMessage({id: "week"}),
                range: {
                    from: startOfWeek(new Date()),
                    to: endOfWeek(new Date()),
                },
            },
            {
                name: intl.formatMessage({id: "month"}),
                range: {
                    from: startOfMonth(new Date()),
                    to: endOfMonth(new Date()),
                },
            },
            {
                name: intl.formatMessage({id: "quarter"}),
                range: {
                    from: startOfQuarter(new Date()),
                    to: endOfQuarter(new Date())
                },
            },
            {
                name: intl.formatMessage({id: "year"}),
                range: {
                    from: startOfYear(new Date()),
                    to: endOfYear(new Date())
                },
            },
        ];

        return ranges.map((r: RangePickerPreset) => ({
            ...r,
            buttonProps: {
                props: {
                    ...applyTooltip(intl.formatMessage({id: "dateRange"}, {
                        from: formatDate(r.range?.from),
                        to: formatDate(r.range?.to)
                    })),
                },
            },
        }));
    }, [intl, applyTooltip]);

    return (
        <DatePicker
            title={intl.formatMessage({id: "pickARange"})}
            cancelButton={{text: intl.formatMessage({id: "cancel"})}}
            continueButton={{text: intl.formatMessage({id: "apply"})}}
            show={showDatePicker}
            setShow={setShowDatePicker}
            calendarLocale={nl}
            horizontalViewBreakpoint={"xl"}
            withTimeConfig={{enabled: false}}
            pickerConfig={{
                type: "range",
                defaultRange: dateRange,
                onContinue: (selectedRange: DateRange | undefined) => {
                    if (!selectedRange || !selectedRange.from || !selectedRange.to) {
                        return;
                    }

                    setSearchParamsDateRange({
                        from: startOfDay(selectedRange.from),
                        to: endOfDay(selectedRange.to),
                    }, setSearchParams);
                },
                presets: dateRangePresets,
            }}
        />
    );
};

export default StatisticsRangePicker;